/**
 * TypeScript mirror of the CSS tokens (TZ 8.12.1). Charts (canvas) cannot read CSS classes, so they resolve the
 * live CSS variable values through `readThemeTokens`, which keeps company --primary overrides and dark mode in sync.
 */

export type ColorScheme = 'light' | 'dark';

export interface ThemeTokenValues {
  background: string;
  card: string;
  ink: string;
  muted: string;
  hair: string;
  primary: string;
  compare: string;
  good: string;
  bad: string;
  warn: string;
  categorical: readonly string[];
  heat: readonly string[];
  fontSans: string;
  fontDisplay: string;
  fontMono: string;
}

/** Static values of 8.12.1, used for tests and as fallbacks when no DOM is available. */
export const STATIC_THEME_TOKENS: Record<ColorScheme, ThemeTokenValues> = {
  light: {
    background: '#f4f6fa',
    card: '#ffffff',
    ink: '#0d1220',
    muted: '#54607a',
    hair: 'rgba(13, 18, 32, 0.1)',
    primary: '#1f6fd4',
    compare: '#9aa4b5',
    good: '#1a7f37',
    bad: '#c0392b',
    warn: '#b7791f',
    categorical: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'],
    heat: ['#eef4fd', '#cde2fb', '#9ec5f4', '#6da7ec', '#3987e5', '#256abf', '#184f95', '#0d366b'],
    fontSans: 'Inter, system-ui, sans-serif',
    fontDisplay: 'Space Grotesk, Inter, sans-serif',
    fontMono: 'Space Mono, monospace',
  },
  dark: {
    background: '#0b0f17',
    card: '#141a24',
    ink: '#e7ecf3',
    muted: '#8a96ad',
    hair: 'rgba(255, 255, 255, 0.08)',
    primary: '#6aa9ff',
    compare: '#5b6577',
    good: '#3fb950',
    bad: '#f85149',
    warn: '#e3b341',
    categorical: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'],
    heat: ['#1a2332', '#182f4d', '#1a3c69', '#1c4a86', '#1f58a3', '#2467c1', '#2b77de', '#3a89f7'],
    fontSans: 'Inter, system-ui, sans-serif',
    fontDisplay: 'Space Grotesk, Inter, sans-serif',
    fontMono: 'Space Mono, monospace',
  },
};

export const CATEGORICAL_PALETTE_SIZE = 8; // 8.12.1: up to 8 stores, then grouping

function readCssVariable(
  styleDeclaration: CSSStyleDeclaration,
  variableName: string,
  fallback: string,
): string {
  const variableValue = styleDeclaration.getPropertyValue(variableName).trim();
  return variableValue === '' ? fallback : variableValue;
}

/** Resolves the current token values from the document root (respects dark mode and brand overrides). */
export function readThemeTokens(colorScheme: ColorScheme, rootElement?: HTMLElement): ThemeTokenValues {
  const fallback = STATIC_THEME_TOKENS[colorScheme];
  if (typeof window === 'undefined') {
    return fallback;
  }
  const styleDeclaration = window.getComputedStyle(rootElement ?? document.documentElement);
  const read = (variableName: string, fallbackValue: string): string =>
    readCssVariable(styleDeclaration, variableName, fallbackValue);
  return {
    background: read('--bg', fallback.background),
    card: read('--card', fallback.card),
    ink: read('--ink', fallback.ink),
    muted: read('--muted', fallback.muted),
    hair: read('--hair', fallback.hair),
    primary: read('--primary', fallback.primary),
    compare: read('--compare', fallback.compare),
    good: read('--good', fallback.good),
    bad: read('--bad', fallback.bad),
    warn: read('--warn', fallback.warn),
    categorical: fallback.categorical.map((fallbackColor, slotIndex) =>
      read(`--series-${slotIndex + 1}`, fallbackColor),
    ),
    heat: fallback.heat.map((fallbackColor, stepIndex) => read(`--heat-${stepIndex}`, fallbackColor)),
    fontSans: read('--font-sans', fallback.fontSans),
    fontDisplay: read('--font-display', fallback.fontDisplay),
    fontMono: read('--font-mono', fallback.fontMono),
  };
}

/**
 * Stable colour of a store (8.4: "Rang har do'konga doimiy biriktiriladi"). The backend assigns `color_index`
 * once per store; the colour never depends on rank or on how many stores are selected.
 */
export function categoricalColorForIndex(colorIndex: number, palette: readonly string[]): string {
  const paletteLength = palette.length;
  const slotIndex = ((colorIndex % paletteLength) + paletteLength) % paletteLength;
  return palette[slotIndex] ?? '#9aa4b5';
}

/** CSS variable reference for a stable store colour, for DOM elements (legend dots, table swatches). */
export function categoricalCssVariable(colorIndex: number): string {
  const slotIndex =
    ((colorIndex % CATEGORICAL_PALETTE_SIZE) + CATEGORICAL_PALETTE_SIZE) % CATEGORICAL_PALETTE_SIZE;
  return `var(--series-${slotIndex + 1})`;
}
